// Checks for a recorded 3D demo (a member's own recording, shared data): one malformed clip must never freeze or
// break the exercise page for anyone. The app shows no demo for a clip that fails (apps/web/src/demo/registry.ts),
// the save rules refuse it, and the database checks the same bounds on write (fitness.valid_recorded_demo).
import type { DemoClip, RecordedDemo } from './types';

/** Bounds of a recorded clip. A real rep is 1 to 10 s at 6 to 15 frames a second. */
export const CLIP_LIMITS = { maxFrames: 900, minFps: 1, maxFps: 60, maxTracks: 64, maxValue: 100, maxRadius: 10 } as const;

const TRACK = /^[A-Za-z0-9_-]{1,40}\.(quaternion|position)$/;
const POSITION_TRACKS = new Set(['root.position', 'kettlebell.position']);
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The first problem of a clip in plain words, or null when the viewer can play it safely. */
export function clipProblem(clip: unknown): string | null {
  if (!isObj(clip)) return 'clip is not an object';
  if (clip.v !== 1) return 'unknown clip version';
  if (typeof clip.id !== 'string' || clip.id.length > 100) return 'clip without an id';
  const frames = clip.frames;
  if (!Number.isInteger(frames) || (frames as number) < 2 || (frames as number) > CLIP_LIMITS.maxFrames) return 'frame count out of range';
  const n = frames as number;
  if (!finite(clip.fps) || clip.fps < CLIP_LIMITS.minFps || clip.fps > CLIP_LIMITS.maxFps) return 'frame rate out of range';
  if (!finite(clip.still) || clip.still < 0 || clip.still > 1) return 'still frame out of range';
  const box = clip.frame;
  if (!isObj(box) || !Array.isArray(box.center) || box.center.length !== 3 || !box.center.every((c) => finite(c) && Math.abs(c) <= CLIP_LIMITS.maxValue)) return 'no camera box';
  if (!finite(box.radius) || box.radius <= 0 || box.radius > CLIP_LIMITS.maxRadius) return 'camera box radius out of range';
  if (clip.kettlebell !== undefined && typeof clip.kettlebell !== 'boolean') return 'kettlebell is not true or false';
  if (clip.scene !== undefined) {
    const scene = clip.scene;
    if (!Array.isArray(scene) || scene.length > 12) return 'scene is not a short list of boxes';
    for (const b of scene) {
      if (!isObj(b) || !Array.isArray(b.at) || !Array.isArray(b.size) || b.at.length !== 3 || b.size.length !== 3) return 'scene box without a place and size';
      if (!b.at.every((v) => finite(v) && Math.abs(v) <= CLIP_LIMITS.maxRadius) || !b.size.every((v) => finite(v) && v > 0 && v <= 5)) return 'scene box out of range';
    }
  }
  const tracks = clip.tracks;
  if (!isObj(tracks)) return 'no tracks';
  const names = Object.keys(tracks);
  if (names.length < 1 || names.length > CLIP_LIMITS.maxTracks) return 'track count out of range';
  for (const name of names) {
    if (!TRACK.test(name)) return `unknown track ${name.slice(0, 40)}`;
    const quat = name.endsWith('.quaternion');
    if (!quat && !POSITION_TRACKS.has(name)) return `unknown track ${name}`;
    const values = tracks[name];
    if (!Array.isArray(values) || values.length !== n * (quat ? 4 : 3)) return `track ${name} does not match the frame count`;
    for (const x of values) if (!finite(x) || Math.abs(x) > CLIP_LIMITS.maxValue) return `track ${name} holds a value that is not a usable number`;
  }
  return null;
}

/** The first problem of a recorded demo (the wrapper and its clip), or null. */
export function recordedDemoProblem(demo: unknown): string | null {
  if (!isObj(demo) || demo.source !== 'recording') return 'not a recorded demo';
  if (typeof demo.id !== 'string' || !demo.id || demo.id.length > 100) return 'demo without an id';
  if (typeof demo.license !== 'string' || !demo.license) return 'demo without a license';
  if (typeof demo.date !== 'string') return 'demo without a date';
  if (typeof demo.sideView !== 'boolean') return 'demo without the side view setting';
  return clipProblem(demo.clip);
}

/** Narrowing helper: a recorded demo that passed the checks. */
export function isValidRecordedDemo(demo: unknown): demo is RecordedDemo & { clip: DemoClip } {
  return recordedDemoProblem(demo) === null;
}
